import type { EditorCommand } from '@atlaskit/editor-common/types';
import type { IDMap } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown/serializer';

import type {
	AllowedAIEvents,
	CompleteAIEvent,
	StreamAIEvent,
	ErrorAIEvent,
} from '../../editor-actions/types';

import { createCommand } from './ai-session-context-plugin';
import type { AIContentPositionEntry } from './types';

export const resetSessionState = (): EditorCommand => createCommand({ type: 'RESET' });

export const streamADFResponse = ({ adfResponse, markdown }: StreamAIEvent): EditorCommand =>
	createCommand({ type: 'STREAM', adfResponse, markdown });
export const completeADFStream = ({
	adfResponse,
	markdown,
	responseTooSimilar,
	forceAbortedToolCallId,
}: CompleteAIEvent): EditorCommand =>
	createCommand({
		type: 'COMPLETE',
		adfResponse,
		markdown,
		responseTooSimilar,
		forceAbortedToolCallId,
	});
export const errorADFStream = ({ errorInfo }: ErrorAIEvent): EditorCommand =>
	createCommand({ type: 'ERROR', errorInfo });

// ==== Only used for prompts using the internal streaming infrastructure ====
// These can be deleted when all prompts are migrated to use the orchestrator.
export const updateTraceIds = (traceIds: string[]): EditorCommand =>
	createCommand({ type: 'UPDATE_TRACE_IDS', traceIds });
export const updateIdMap = (idMap: IDMap): EditorCommand =>
	createCommand({ type: 'UPDATE_ID_MAP', idMap });
export const cancelStream = (): EditorCommand => createCommand({ type: 'CANCEL_STREAM' });
export const pauseStream = (): EditorCommand => createCommand({ type: 'PAUSE_STREAM' });
export const resumeStream = (): EditorCommand => createCommand({ type: 'RESUME_STREAM' });
export const setHasUserScrolled = (): EditorCommand =>
	createCommand({ type: 'SET_HAS_USER_SCROLLED' });
export const startFreeGenStream = (userInput: string): EditorCommand =>
	createCommand({ type: 'START_FREE_GEN_STREAM', userInput });

// [CCI-15904] New AIFC Editor Experience — Post Stream Review commands.
export const enterPostStreamReview = (segmentIndex: number = 0): EditorCommand =>
	createCommand({
		type: 'POST_STREAM_REVIEW',
		currentStreamReviewSegmentIndex: segmentIndex,
	});
export const updateStreamReviewSegment = (segmentIndex: number): EditorCommand =>
	createCommand({
		type: 'UPDATE_STREAM_REVIEW_SEGMENT',
		currentStreamReviewSegmentIndex: segmentIndex,
	});
export const updateAIContentPosition = (
	index: number,
	startPos: number,
	endPos: number,
): EditorCommand =>
	createCommand({
		type: 'UPDATE_AI_CONTENT_POSITION',
		index,
		startPos,
		endPos,
	});
// Clear the stored positions of a tool call aborted by the queue timeout, once
// it has been resumed.
export const clearAbortedToolCallPosition = (toolCallId: string): EditorCommand =>
	createCommand({
		type: 'CLEAR_ABORTED_TOOL_CALL_POSITION',
		toolCallId,
	});

// Set (or clear, with `[]`) the fine step-through segments the Post
// Stream Review toolbar navigates. Called on entering / leaving step-through.
export const setStreamReviewSegments = (segments: AIContentPositionEntry[]): EditorCommand =>
	createCommand({
		type: 'SET_STREAM_REVIEW_SEGMENTS',
		segments,
	});

/**
 * Dispatches an allowed AI event by converting it to the appropriate command.
 * Only 'stream', 'complete', and 'error' events are supported. These events are
 * dispatched by the AI streaming orchestrator.
 *
 * @param event - The AI event to dispatch
 * @returns The corresponding EditorCommand, or undefined if the event type is not supported
 */
export const dispatchAllowedStreamingEvent = (
	event: AllowedAIEvents,
): EditorCommand | undefined => {
	switch (event.type) {
		case 'stream':
			return streamADFResponse(event);
		case 'complete':
			return completeADFStream(event);
		case 'error':
			return errorADFStream(event);
		default: {
			assertUnreachable(event);
			return undefined;
		}
	}
};

function assertUnreachable(_x: never) {}
